// The newest-first media grid + the "load more" affordance. Presentational: it
// renders whatever `items` it is handed (the useMedia hook already sorts them
// newest-first) and calls `onLoadMore` when the cursor says there's another page —
// automatically as the end of the grid nears the viewport (infinite scroll), with
// the button kept as the visible progress indicator + no-IntersectionObserver fallback.
//
// B4 adds the Mosaic picker: a toolbar toggles a select mode where each card shows
// a tappable pick badge (tap to place a cell, tap to remove); the picked items, in
// tap order, open the Mosaic overlay (→ a WebCodecs MP4). All additive — outside
// the picker the grid behaves exactly as in B3.

import { useEffect, useRef, useState } from "react";
import type { ApiClient, MediaItem, MediaCounts, MediaTypeFilter } from "../api";
import { isClip, MOSAIC_MAX_SELECTION } from "../cores";
import type { SheetCores } from "../cores";
import type {
  BuildMosaicOptions,
  MosaicSelectionItem,
} from "../cores";
import { useMosaicSelection } from "../useMosaicSelection";
import { useSelection } from "../useSelection";
import { downloadZip } from "../download";
import { MediaCard } from "./MediaCard";
import { MediaLightbox } from "./MediaLightbox";
import { Mosaic } from "./Mosaic";
import { AddToAlbumModal } from "./AddToAlbumModal";

export interface MediaGridProps {
  items: MediaItem[];
  /** Non-null ⇢ another page is available; renders the "Load more" button. */
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  /** Forwarded to each card so tests can stub the download. */
  onDownload?: (item: MediaItem) => Promise<void>;
  /** Forwarded to each card so tests can stub the contact-Sheet decode/render seam. */
  sheetCores?: SheetCores;
  /** Forwarded to the Mosaic overlay so tests can stub the WebCodecs encode. */
  buildMosaic?: (
    items: MosaicSelectionItem[],
    options: BuildMosaicOptions,
  ) => Promise<Blob>;
  /** Override the zip-download flow (tests stub it); default fetches + zips for real. May
   *  return a ZipReport ({zipped, skipped}) so partial batches are reported honestly. */
  onDownloadZip?: (items: MediaItem[]) => Promise<{ zipped: number; skipped: number } | void>;
  /** Take an item into the Look editor (forwarded to each card's detail lightbox). */
  onUseInEditor?: (item: MediaItem) => void;
  /** Re-list the gallery (fresh presigned URLs) when a tile's URL expired (#136). */
  onReload?: () => void;
  /** The active type filter (#136). Server-side: the parent re-fetches on change. Default "all". */
  filter?: MediaTypeFilter;
  /** Change the type filter (drives a server-side re-fetch in the parent). */
  onFilter?: (filter: MediaTypeFilter) => void;
  /** Whole-library per-type counts for the filter tabs; falls back to the loaded items. */
  counts?: MediaCounts;
  /** The filtered library size (for "showing N of M"); falls back to the loaded count. */
  total?: number;
  /** A filter-change / reload is in flight — dim the grid + mark it busy, but keep the
   *  tabs + previous items on screen (don't swap in the full-screen loader). */
  refetching?: boolean;
  /** Archive visibility (ADR-0058): true → the Archived view; the per-card action becomes Restore. */
  archivedView?: boolean;
  /** Switch between the default gallery and the Archived view. */
  onToggleArchived?: () => void;
  /** How many items are Archived — the "Archived (N)" toggle badge. */
  archivedCount?: number;
  /** Archive the given keys (hide them from the gallery). */
  onArchive?: (keys: string[]) => Promise<void>;
  /** Restore the given keys (un-hide them). */
  onRestore?: (keys: string[]) => Promise<void>;
  /** An archive/restore mutation is in flight — disable the archive actions. */
  mutating?: boolean;
  /**
   * The api client — threaded ONLY for the Select-mode "Add to album…" picker (ADR-0059), which
   * lists/creates albums + adds the Selection. Optional so the grid stays presentational + the
   * existing tests (which omit it) are unaffected; the button shows only when it's wired.
   */
  client?: ApiClient;
  /**
   * Show the "Mosaic newest" convenience button (seed the newest 16 + open the overlay). Default
   * true (the owner gallery). The public share viewer passes false — a visitor picks explicitly.
   */
  showMosaicNewest?: boolean;
}

export function MediaGrid({
  items,
  hasMore,
  loadingMore,
  onLoadMore,
  onDownload,
  sheetCores,
  buildMosaic,
  onDownloadZip,
  onUseInEditor,
  onReload,
  filter = "all",
  onFilter,
  counts,
  total,
  refetching = false,
  archivedView = false,
  onToggleArchived,
  archivedCount = 0,
  onArchive,
  onRestore,
  mutating = false,
  client,
  showMosaicNewest = true,
}: MediaGridProps): React.JSX.Element {
  const pick = useMosaicSelection();
  const select = useSelection();
  const [mosaicOpen, setMosaicOpen] = useState(false);
  const [addAlbumOpen, setAddAlbumOpen] = useState(false);
  const [zipBusy, setZipBusy] = useState(false);
  const [zipError, setZipError] = useState<string | null>(null);
  // A transient action confirmation ("Added 3 to “Summer” (2 already there)", "Zipped 6 of 8…").
  // The add-to-album counts were previously discarded (success had zero feedback, a dedup was
  // invisible) and a partial ZIP had no way to report its skips.
  const [notice, setNotice] = useState<string | null>(null);
  const noticeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (noticeTimer.current) clearTimeout(noticeTimer.current);
    },
    [],
  );
  function showNotice(text: string): void {
    setNotice(text);
    if (noticeTimer.current) clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(null), 6000);
  }
  // The grid — not each card — owns the open detail lightbox (as an index into `items`), so
  // the lightbox can step to a sibling; a card only requests an open via `onOpen`.
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  // A refresh (filter change / reload) can hand us a SHORTER `items` while the lightbox is
  // open — close rather than index past the end (clamp-not-crash).
  useEffect(() => {
    if (openIdx != null && openIdx >= items.length) setOpenIdx(null);
  }, [openIdx, items.length]);

  // The item under the open index (undefined once out of range — the effect above closes it).
  const openItem = openIdx != null ? items[openIdx] : undefined;

  // A filter change swaps the visible set under an active selection, and the three bulk
  // sinks would then disagree about scope: ZIP resolves against the VISIBLE items while
  // Archive / Add-to-album act on the full key set — so keys picked under another filter
  // silently vanish from a ZIP but not from an Archive. Clear the picked keys (both modes
  // stay active — the point of in-mode tabs is scoping WHAT you're about to pick).
  const filterRef = useRef(filter);
  useEffect(() => {
    if (filterRef.current === filter) return;
    filterRef.current = filter;
    if (select.active) select.clear();
    if (pick.active) pick.clear();
  }, [filter, select, pick]);

  // Infinite scroll: when the sentinel by the Load-more button nears the viewport, fetch the
  // next page — the button stays as the visible progress indicator and the fallback (jsdom /
  // no-IntersectionObserver browsers keep the manual path). The effect re-runs when a page
  // lands (loadingMore flips, items grow), so a sentinel still in view — a tall window whose
  // first page didn't fill it — immediately loads the next page until the viewport fills.
  const moreRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = moreRef.current;
    if (!el || !hasMore || loadingMore) return;
    if (typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) onLoadMore();
      },
      // Start the fetch well before the owner reaches the edge, so scrolling rarely stalls.
      { rootMargin: "600px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loadingMore, items.length, onLoadMore]);

  // Preload nicety: while the lightbox is open, warm the neighbouring items' presigned URLs so
  // arrowing to them is instant. crossOrigin matches the grid / lightbox <img> (ADR-0022) so the
  // warmed response is the SAME cacheable CORS entry the <img> reuses (a bare fetch would cache a
  // non-CORS response and force a re-fetch).
  useEffect(() => {
    if (openIdx == null) return;
    for (const neighbor of [items[openIdx - 1], items[openIdx + 1]]) {
      if (!neighbor) continue;
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = neighbor.url;
    }
  }, [openIdx, items]);

  // The server already filtered `items` by `filter` (#136/#141), so the grid renders them
  // directly. Tab counts come from the server (whole-library); fall back to the loaded
  // items when a caller omits them. Select-all / range scope to the loaded set.
  const tabCounts = counts ?? countsOf(items);

  // Click a card in select mode: shift-click extends from the anchor over the (visible) gallery
  // order (a whole range in two clicks); a plain click toggles one + moves the anchor.
  function handleSelect(item: MediaItem, opts: { range: boolean }): void {
    if (opts.range && select.anchor) {
      const order = items.map((i) => i.key);
      const a = order.indexOf(select.anchor);
      const b = order.indexOf(item.key);
      if (a >= 0 && b >= 0) {
        const [lo, hi] = a <= b ? [a, b] : [b, a];
        select.addRange(order.slice(lo, hi + 1), item.key);
        return;
      }
    }
    select.toggle(item.key);
  }

  const allKeys = items.map((i) => i.key);
  const allSelected = allKeys.length > 0 && allKeys.every((k) => select.has(k));

  // The filter tabs — rendered in EVERY toolbar mode (they used to vanish in Select/Mosaic,
  // so a selection couldn't be scoped by type). .gallery__filter's margin-left:auto keeps
  // them pinned right; a filter change in select mode just re-fetches under the selection.
  const filterTabs = (
    <div className="gallery__filter" role="group" aria-label="Filter media">
      {(["all", "photos", "clips"] as const).map((f) => (
        <button
          key={f}
          type="button"
          className={`gallery__filter-btn${filter === f ? " is-active" : ""}`}
          aria-pressed={filter === f}
          data-testid={`filter-${f}`}
          onClick={() => onFilter?.(f)}
        >
          {f === "all" ? "All" : f === "photos" ? "Photos" : "Clips"} ({tabCounts[f]})
        </button>
      ))}
    </div>
  );

  async function handleZip(): Promise<void> {
    const chosen = select.resolve(items);
    if (chosen.length === 0) return;
    setZipBusy(true);
    setZipError(null);
    try {
      const report = await (onDownloadZip ?? downloadZip)(chosen);
      select.cancel();
      // A partial batch (some presigned URLs expired) is reported, not silently reshaped.
      if (report && report.skipped > 0) {
        showNotice(
          `Zipped ${report.zipped} of ${chosen.length} — ${report.skipped} couldn’t be read (try Refresh for fresh links).`,
        );
      }
    } catch (e) {
      setZipError(e instanceof Error ? e.message : "Download failed");
    } finally {
      setZipBusy(false);
    }
  }

  // Bulk Archive/Restore the current Selection (ADR-0058) — the second Select-mode sink beside ZIP.
  // In the Archived view the same pick Restores; corrupt cards are already excluded from Select.
  async function handleBulkArchive(): Promise<void> {
    if (select.keys.length === 0) return;
    await (archivedView ? onRestore : onArchive)?.(select.keys);
    select.cancel();
  }

  return (
    <div className="gallery">
      <div className="gallery__bar">
        {pick.active ? (
          <>
            <button
              type="button"
              className="btn btn--primary"
              data-testid="make-mosaic"
              disabled={pick.keys.length === 0}
              onClick={() => setMosaicOpen(true)}
            >
              Make Mosaic ({pick.keys.length})
            </button>
            <button type="button" className="btn" onClick={pick.cancel}>
              Cancel
            </button>
            <button
              type="button"
              className="btn"
              onClick={pick.clear}
              disabled={pick.keys.length === 0}
              data-testid="mosaic-clear"
            >
              Clear
            </button>
            {/* Live selection budget (#138): show n/32 from the first pick, and explain the
                no-op at the cap (the 33rd tap silently did nothing before). */}
            <span className="gallery__hint" data-testid="mosaic-budget" aria-live="polite">
              {pick.keys.length} / {MOSAIC_MAX_SELECTION}
              {pick.full ? " — at the max; remove one to add another" : ""}
            </span>
            {filterTabs}
          </>
        ) : select.active ? (
          <>
            <button
              type="button"
              className="btn btn--primary"
              data-testid="download-zip"
              disabled={select.keys.length === 0 || zipBusy}
              onClick={handleZip}
            >
              {zipBusy
                ? "Zipping…"
                : `Download ZIP (${select.keys.length})`}
            </button>
            {/* The second Select-mode sink (ADR-0058): Archive (default view) or Restore (Archived view)
                the whole Selection in one batch call. */}
            {(archivedView ? onRestore : onArchive) && (
              <button
                type="button"
                className="btn"
                data-testid="bulk-archive"
                disabled={select.keys.length === 0 || zipBusy || mutating}
                onClick={handleBulkArchive}
              >
                {archivedView
                  ? `Restore (${select.keys.length})`
                  : `Archive (${select.keys.length})`}
              </button>
            )}
            {/* Bulk "Add to album" (ADR-0059) — opens the picker for the whole Selection. Shown only
                when the client is wired (the grid stays presentational without it). */}
            {client && (
              <button
                type="button"
                className="btn"
                data-testid="add-to-album"
                disabled={select.keys.length === 0 || zipBusy}
                onClick={() => setAddAlbumOpen(true)}
              >
                Add to album…
              </button>
            )}
            <button
              type="button"
              className="btn"
              data-testid="select-all"
              onClick={() => (allSelected ? select.clear() : select.setAll(allKeys))}
              disabled={zipBusy || allKeys.length === 0}
            >
              {/* With unloaded pages remaining, "Select all" would silently mean "the loaded
                  part" — say what it actually selects. */}
              {allSelected ? "Clear" : hasMore ? `Select loaded (${allKeys.length})` : "Select all"}
            </button>
            <button
              type="button"
              className="btn"
              onClick={select.cancel}
              disabled={zipBusy}
            >
              Cancel
            </button>
            {/* The range hint stays put; a zip error shows ALONGSIDE it, not in its place
                (the old swap hid the hint exactly when the owner was mid-pick). */}
            <span className="gallery__hint">Tap to select · Shift-click for a range</span>
            {zipError && (
              <span className="gallery__hint gallery__hint--error" role="alert">
                {zipError}
              </span>
            )}
            {filterTabs}
          </>
        ) : (
          <>
            {/* Action buttons on the LEFT, the filter tabs on the RIGHT (see .gallery__filter
                margin-left:auto). The filter group renders LAST so it sits at the far right. */}
            <button
              type="button"
              className="btn"
              data-testid="start-mosaic"
              onClick={pick.begin}
            >
              Mosaic
            </button>
            {/* Zero-config path (#138): seed the newest 16 and open the overlay straight away.
                Hidden on the public share viewer (a visitor picks explicitly). */}
            {showMosaicNewest && (
              <button
                type="button"
                className="btn"
                data-testid="mosaic-newest"
                disabled={items.length === 0}
                onClick={() => {
                  pick.seedNewest(items, 16);
                  setMosaicOpen(true);
                }}
              >
                Mosaic newest
              </button>
            )}
            <button
              type="button"
              className="btn"
              data-testid="start-select"
              onClick={select.begin}
            >
              Select
            </button>
            {/* The Archive toggle (ADR-0058): a VISIBILITY axis orthogonal to the kind tabs. Shown
                only when something is archived (or while in the Archived view). In the Archived view
                it becomes the way back to the default gallery. */}
            {(archivedView || archivedCount > 0) && onToggleArchived && (
              <button
                type="button"
                className={"btn gallery__archived-toggle" + (archivedView ? " is-active" : "")}
                data-testid="toggle-archived"
                aria-pressed={archivedView}
                onClick={onToggleArchived}
              >
                {archivedView ? "← Gallery" : `Archived (${archivedCount})`}
              </button>
            )}
            {/* Filter tabs LAST → pushed to the far right by .gallery__filter margin-left:auto. */}
            {filterTabs}
          </>
        )}
      </div>

      {archivedView && (
        <p className="gallery__notice" data-testid="archived-explainer">
          Archived media is hidden from your gallery and removed from the camera’s card on its next
          Sync. The cloud copy is kept — Restore brings it back here, but not onto the camera.
        </p>
      )}

      {notice && (
        <p className="gallery__notice" role="status" data-testid="gallery-notice">
          {notice}
        </p>
      )}

      {/* One persistent live region announces the state (count or filter-empty); the visible
          count / empty nodes mount-and-unmount and so can't reliably re-announce themselves. */}
      <div className="visually-hidden" aria-live="polite" data-testid="gallery-announce">
        {items.length === 0
          ? `No ${filter === "clips" ? "Clips" : "Photos"} yet`
          : `Showing ${items.length}${total != null && total > items.length ? ` of ${total}` : ""}`}
      </div>

      {items.length === 0 ? (
        // The library isn't empty (Gallery handles that), so this is a filter that
        // matched nothing — keep the tabs above visible to switch back.
        <p className="gallery__empty-filter" data-testid="filter-empty">
          {archivedView
            ? "No archived media — nothing hidden here."
            : `No ${filter === "clips" ? "Clips" : "Photos"} yet — switch the filter above.`}
        </p>
      ) : (
        <ul
          className={"grid" + (refetching ? " is-refetching" : "")}
          data-testid="media-grid"
          aria-busy={refetching}
        >
          {items.map((item, index) => (
            <li className="grid__cell" key={item.key}>
              <MediaCard
                item={item}
                onDownload={onDownload}
                sheetCores={sheetCores}
                onOpen={() => setOpenIdx(index)}
                onReload={onReload}
                picking={pick.active}
                pickPosition={pick.positionOf(item.key)}
                onPick={() => pick.toggle(item.key)}
                selecting={select.active}
                selected={select.has(item.key)}
                onSelect={handleSelect}
                archivedView={archivedView}
                onArchive={onArchive ? (it) => void onArchive([it.key]) : undefined}
                onRestore={onRestore ? (it) => void onRestore([it.key]) : undefined}
                mutating={mutating}
              />
            </li>
          ))}
        </ul>
      )}

      {items.length > 0 && (
        <p className="gallery__count" data-testid="gallery-count">
          Showing {items.length}
          {total != null && total > items.length ? ` of ${total}` : ""}
          {!hasMore ? " · end of gallery" : ""}
        </p>
      )}

      {hasMore && (
        <div className="gallery__more">
          <div ref={moreRef} data-testid="load-more-sentinel" aria-hidden="true" />
          <button
            type="button"
            className="btn"
            onClick={onLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? "Loading…" : "Load more"}
          </button>
        </div>
      )}

      {mosaicOpen && (
        <Mosaic
          items={pick.resolve(items)}
          onClose={() => setMosaicOpen(false)}
          onReorder={pick.reorder}
          buildMosaic={buildMosaic}
          decodeClip={sheetCores?.decodeClip}
        />
      )}

      {addAlbumOpen && client && (
        <AddToAlbumModal
          client={client}
          keys={select.keys}
          onClose={() => setAddAlbumOpen(false)}
          onAdded={({ name, added }) => {
            // Mirror handleZip's success: leave the picker + drop out of select mode — and
            // confirm what actually happened (added < picked ⇢ the rest were already there).
            const skipped = select.keys.length - added;
            setAddAlbumOpen(false);
            select.cancel();
            showNotice(
              skipped > 0
                ? `Added ${added} to “${name}” (${skipped} already there).`
                : `Added ${added} to “${name}”.`,
            );
          }}
        />
      )}

      {openIdx != null && openItem && (
        <MediaLightbox
          item={openItem}
          onClose={() => setOpenIdx(null)}
          onDownload={onDownload}
          onUseInEditor={onUseInEditor}
          archivedView={archivedView}
          onArchive={onArchive ? (it) => void onArchive([it.key]) : undefined}
          onRestore={onRestore ? (it) => void onRestore([it.key]) : undefined}
          mutating={mutating}
          // Clamp at the loaded ends — pass a neighbour handler ONLY when that item is
          // already loaded, so arrows/chevrons/swipe are no-ops at the edge (no wrap, and
          // never paginate; matches the on-device Library's clamped Prev/Next).
          onPrev={openIdx > 0 ? () => setOpenIdx(openIdx - 1) : undefined}
          onNext={openIdx < items.length - 1 ? () => setOpenIdx(openIdx + 1) : undefined}
          // The loaded list is a newest-first PREFIX of the library, so the 1-based index is
          // globally correct — but the denominator must be the server's filtered total, not
          // the loaded count ("3 / 40" while the header says "Showing 200" would lie).
          position={openIdx + 1}
          total={total ?? items.length}
        />
      )}
    </div>
  );
}

/** Per-type counts of the loaded items — the fallback when the server omits counts. */
function countsOf(items: MediaItem[]): MediaCounts {
  let photos = 0;
  let clips = 0;
  for (const it of items) {
    if (isClip(it.key)) clips++;
    else photos++;
  }
  return { all: items.length, photos, clips };
}
